import { useState, type MouseEvent } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components/macro'

import { Button, Layout } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import { screen } from 'legacy/styles/screen'

import { canConfirmLoad, type Load } from '../domain/Load'
import { useTenderConfirmation } from '../hooks/useTenderConfirmation'
import { TenderAcceptDialog } from './TenderAcceptDialog'
import { TenderRejectDialog } from './TenderRejectDialog'

export const StyledLayoutGroup = styled(Layout.Group)`
  width: 100%;

  ${screen.md} {
    width: fit-content;
  }
`

export const TenderAnalyticsContext = {
  TenderDetails: '',
  LoadBoard: 'LoadBoard',
} as const

export type TenderConfirmationProps = {
  context?: (typeof TenderAnalyticsContext)[keyof typeof TenderAnalyticsContext]
  disabled?: boolean
  load: Load
  onAccept?: (load: Load) => void
  onReject?: (load: Load) => void
}

export function TenderConfirmation({
  context,
  disabled = false,
  load,
  onAccept,
  onReject,
}: TenderConfirmationProps) {
  const { push: navigate } = useHistory()
  const { acceptTender, acceptStatus, rejectTender, rejectStatus } = useTenderConfirmation(load.id)
  const [isAcceptModalOpen, setAcceptModalOpen] = useState(false)
  const [isRejectModalOpen, setRejectModalOpen] = useState(false)

  const isDisabled = disabled || !canConfirmLoad(load)

  function handleAcceptClick(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
    setAcceptModalOpen(true)
  }

  async function handleAcceptConfirm() {
    try {
      const { carrier1xpLink } = await acceptTender()

      toast.success('Tender accepted successfully')
      setAcceptModalOpen(false)
      onAccept?.(load)

      if (carrier1xpLink && load.shareDetails) {
        const uuidWithToken = carrier1xpLink?.split('/shipment/')[1]
        const uuid = uuidWithToken?.split('?')[0]
        navigate(`/all-in-one/shipment/${uuid}`)
      }
    } catch {
      toast.error('Failed to accept the tender. Please try again.')
    }
  }

  function handleAcceptCancel() {
    setAcceptModalOpen(false)
  }

  function handleRejectClick(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
    setRejectModalOpen(true)
  }

  async function handleRejectConfirm(rejectReason: string) {
    try {
      await rejectTender(rejectReason)
      toast.success('Tender rejected successfully')
      setRejectModalOpen(false)
      onReject?.(load)
    } catch {
      toast.error('Failed to reject the tender. Please try again.')
    }
  }

  function handleRejectCancel() {
    setRejectModalOpen(false)
  }

  return (
    <StyledLayoutGroup align="flex-end" gap="spacing-3">
      <Button
        type="button"
        variant="secondary"
        onClick={handleRejectClick}
        disabled={isDisabled}
        style={{ flexGrow: 1 }}
      >
        Reject
      </Button>
      <Button
        type="button"
        variant="primary"
        onClick={handleAcceptClick}
        disabled={isDisabled}
        style={{ flexGrow: 1 }}
      >
        Accept
      </Button>
      <TenderAcceptDialog
        open={isAcceptModalOpen}
        load={load}
        onConfirm={handleAcceptConfirm}
        onCancel={handleAcceptCancel}
        loading={acceptStatus === RequestStatus.Pending}
      />
      <TenderRejectDialog
        open={isRejectModalOpen}
        onConfirm={handleRejectConfirm}
        onCancel={handleRejectCancel}
        loading={rejectStatus === RequestStatus.Pending}
      />
    </StyledLayoutGroup>
  )
}
